/**
 * Configure schedule, as in AgencyAnalytics: daily, weekly, bi-weekly,
 * monthly, quarterly or annually; the day; a time in 15-minute steps (in the
 * client's time zone); skip weekends; require approval; let recipients change
 * the dates; then To, Subject and Message. The period each email shows comes
 * from the report's own date picker.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import type { Frequency, Schedule, ScheduleInput } from './email-api.ts';
import { EmailFields, splitRecipients } from './EmailFields.tsx';

const FREQUENCY_LABEL: Record<Frequency, string> = {
  DAILY: 'Daily',
  WEEKLY: 'Weekly',
  BIWEEKLY: 'Every two weeks',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly (first day of the quarter)',
  ANNUALLY: 'Annually',
};
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_IN_LONGEST_MONTH = 31;
const QUARTERS_PER_HOUR = 4;
const MINUTES_PER_STEP = 15;
const HOURS = 24;
const TIMES = Array.from({ length: HOURS * QUARTERS_PER_HOUR }, (_, step) => {
  const hour = Math.floor(step / QUARTERS_PER_HOUR);
  const minute = (step % QUARTERS_PER_HOUR) * MINUTES_PER_STEP;
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
});

const TEEN_LOW = 11;
const TEEN_HIGH = 13;
const SUFFIX: Record<number, string> = { 1: 'st', 2: 'nd', 3: 'rd' };

/** "1st", "22nd", "11th" … and the 31st reads "last day". */
export function ordinal(day: number): string {
  if (day === DAYS_IN_LONGEST_MONTH) return 'last day';
  const teen = day % 100 >= TEEN_LOW && day % 100 <= TEEN_HIGH;
  return `${day}${teen ? 'th' : (SUFFIX[day % 10] ?? 'th')}`;
}

/** "Monthly on the 1st at 08:00" — the summary AgencyAnalytics shows. */
export function describe(
  schedule: Pick<Schedule, 'frequency' | 'day_of_week' | 'day_of_month' | 'month' | 'send_time'>,
): string {
  const at = ` at ${schedule.send_time}`;
  const weekday = WEEKDAYS[(schedule.day_of_week ?? 1) - 1];
  switch (schedule.frequency) {
    case 'DAILY':
      return `Daily${at}`;
    case 'WEEKLY':
      return `Weekly on ${weekday}${at}`;
    case 'BIWEEKLY':
      return `Every two weeks on ${weekday}${at}`;
    case 'MONTHLY':
      return `Monthly on the ${ordinal(schedule.day_of_month ?? 1)}${at}`;
    case 'QUARTERLY':
      return `Quarterly on the 1st${at}`;
    case 'ANNUALLY':
      return `Annually on ${ordinal(schedule.day_of_month ?? 1)} ${MONTHS[(schedule.month ?? 1) - 1]}${at}`;
  }
}

export function ScheduleForm({
  initial,
  defaultSubject,
  onSave,
  onCancel,
}: {
  initial: Schedule | null;
  defaultSubject: string;
  onSave: (input: ScheduleInput) => Promise<void>;
  onCancel: () => void;
}): ReactNode {
  const [value, setValue] = useState<ScheduleInput>({
    frequency: initial?.frequency ?? 'MONTHLY',
    day_of_week: initial?.day_of_week ?? 1,
    day_of_month: initial?.day_of_month ?? 1,
    month: initial?.month ?? 1,
    send_time: initial?.send_time ?? '08:00',
    skip_weekends: initial?.skip_weekends ?? false,
    require_approval: initial?.require_approval ?? false,
    allow_range_change: initial?.allow_range_change ?? false,
    active: initial?.active ?? true,
    recipients: initial?.recipients ?? [],
    subject: initial?.subject ?? defaultSubject,
    message: initial?.message ?? '',
  });
  const [recipientsText, setRecipientsText] = useState((initial?.recipients ?? []).join(', '));
  const [busy, setBusy] = useState(false);
  const set = (patch: Partial<ScheduleInput>) => setValue((current) => ({ ...current, ...patch }));
  const weekly = value.frequency === 'WEEKLY' || value.frequency === 'BIWEEKLY';
  const monthly = value.frequency === 'MONTHLY' || value.frequency === 'ANNUALLY';
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onSave({ ...value, recipients: splitRecipients(recipientsText) });
    } finally {
      setBusy(false);
    }
  };
  const check = (
    key: 'skip_weekends' | 'require_approval' | 'allow_range_change',
    label: string,
  ) => (
    <label className="rs-check">
      <input
        type="checkbox"
        checked={value[key]}
        onChange={(event) => set({ [key]: event.target.checked })}
      />
      {label}
    </label>
  );
  return (
    <form className="rth" onSubmit={(event) => void submit(event)}>
      <label className="rs-field">
        Send
        <select
          value={value.frequency}
          onChange={(event) => set({ frequency: event.target.value as Frequency })}
        >
          {Object.entries(FREQUENCY_LABEL).map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {weekly && (
        <label className="rs-field">
          On
          <select
            value={value.day_of_week ?? 1}
            onChange={(event) => set({ day_of_week: Number(event.target.value) })}
          >
            {WEEKDAYS.map((day, index) => (
              <option key={day} value={index + 1}>
                {day}
              </option>
            ))}
          </select>
        </label>
      )}
      {value.frequency === 'ANNUALLY' && (
        <label className="rs-field">
          Month
          <select
            value={value.month ?? 1}
            onChange={(event) => set({ month: Number(event.target.value) })}
          >
            {MONTHS.map((month, index) => (
              <option key={month} value={index + 1}>
                {month}
              </option>
            ))}
          </select>
        </label>
      )}
      {monthly && (
        <label className="rs-field">
          On the
          <select
            value={value.day_of_month ?? 1}
            onChange={(event) => set({ day_of_month: Number(event.target.value) })}
          >
            {Array.from({ length: DAYS_IN_LONGEST_MONTH }, (_, index) => (
              <option key={index} value={index + 1}>
                {ordinal(index + 1)}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="rs-field">
        At (client’s time zone)
        <select
          value={value.send_time}
          onChange={(event) => set({ send_time: event.target.value })}
        >
          {TIMES.map((time) => (
            <option key={time} value={time}>
              {time}
            </option>
          ))}
        </select>
      </label>
      {check('skip_weekends', 'Skip weekends (send on Monday instead)')}
      {check('require_approval', 'Require approval before it goes out')}
      {check('allow_range_change', 'Allow recipients to change the date range')}
      <EmailFields
        value={value}
        recipientsText={recipientsText}
        onRecipientsText={setRecipientsText}
        onChange={set}
      />
      <div className="sp-actions">
        <button type="submit" className="btn small" disabled={busy}>
          Save schedule
        </button>
        <button type="button" className="btn small secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
